import React from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
} from '@mui/material';
import { useImportWizardStore } from '../../store/useImportWizardStore';
import ColumnSelect from './ColumnSelect';
import type { MappingConfig } from '../../utils/types';

type OwnerStrategy = MappingConfig['ownerStrategy'];

/**
 * Three owner strategy options with conditional column selector.
 * - Organization Default
 * - Column (User ID) — shows column dropdown
 * - Column (Email) — shows column dropdown
 *
 * Validates: Requirements 8.1, 8.2
 */
const OwnerStrategyConfig: React.FC = () => {
  const headers = useImportWizardStore((s) => s.headers);
  const ownerStrategy = useImportWizardStore(
    (s) => s.mappingConfig.ownerStrategy
  );
  const updateMappingConfig = useImportWizardStore(
    (s) => s.updateMappingConfig
  );

  const strategyType = ownerStrategy?.type ?? 'org_default';
  const column =
    ownerStrategy && 'column' in ownerStrategy ? ownerStrategy.column : '';

  const handleStrategyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const type = event.target.value as OwnerStrategy['type'];
    let newStrategy: OwnerStrategy;

    switch (type) {
      case 'column_user_id':
        newStrategy = { type: 'column_user_id', column: '' };
        break;
      case 'column_email':
        newStrategy = { type: 'column_email', column: '' };
        break;
      default:
        newStrategy = { type: 'org_default' };
    }

    updateMappingConfig({ ownerStrategy: newStrategy });
  };

  const handleColumnChange = (value: string) => {
    if (strategyType === 'column_user_id') {
      updateMappingConfig({
        ownerStrategy: { type: 'column_user_id', column: value },
      });
    } else if (strategyType === 'column_email') {
      updateMappingConfig({
        ownerStrategy: { type: 'column_email', column: value },
      });
    }
  };

  const showColumnSelector =
    strategyType === 'column_user_id' || strategyType === 'column_email';

  return (
    <FormControl component="fieldset" fullWidth>
      <FormLabel component="legend" id="owner-strategy-label">
        Owner Assignment Strategy
      </FormLabel>
      <RadioGroup
        aria-labelledby="owner-strategy-label"
        name="ownerStrategy"
        value={strategyType}
        onChange={handleStrategyChange}
      >
        <FormControlLabel
          value="org_default"
          control={<Radio />}
          label="Organization Default"
        />
        <FormControlLabel
          value="column_user_id"
          control={<Radio />}
          label="Column (User ID)"
        />
        <FormControlLabel
          value="column_email"
          control={<Radio />}
          label="Column (Email)"
        />
      </RadioGroup>

      {showColumnSelector && (
        <Box sx={{ mt: 2 }}>
          <ColumnSelect
            label="Owner Column"
            value={column}
            headers={headers}
            onChange={handleColumnChange}
          />
        </Box>
      )}
    </FormControl>
  );
};

export default OwnerStrategyConfig;
